import type { UseFormReturn } from 'react-hook-form';

import { FormField, FormInput, FormInputTrimmed, FormTextarea, Label, LabeledSwitch } from '@/_components';
import { FormInputEmail, FormInputUrl } from '@/_containers';
import { RichText } from '@/_containers/inputs/rich-text';

import type { BrandFormSchema } from './schemas';

export function FormFields({
  form,
  isEditable = true,
  autoFocus = false,
}: {
  form: UseFormReturn<BrandFormSchema>;
  isEditable?: boolean;
  autoFocus?: boolean;
}) {
  const brandId = form.watch('id');

  return (
    <>
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormInput
            formItemClassName="col-span-12 sm:col-span-6"
            label="Brand (required)"
            {...field}
            disabled={!isEditable}
            autoFocus={autoFocus}
          />
        )}
      />
      <FormField
        control={form.control}
        name="legal_name"
        render={({ field }) => (
          <FormInput
            {...field}
            disabled={!isEditable}
            formItemClassName="col-span-12 sm:col-span-6"
            label="Legal Name"
          />
        )}
      />

      <FormField
        control={form.control}
        name="code"
        render={({ field }) => (
          <FormInputTrimmed
            formItemClassName="col-span-12 sm:col-span-6"
            label="Code"
            {...field}
            disabled={!isEditable}
          />
        )}
      />

      <FormField
        control={form.control}
        name="website"
        render={({ field }) => (
          <FormInputUrl
            formItemClassName="col-span-12 sm:col-span-6"
            label="Website"
            {...field}
            disabled={!isEditable}
          />
        )}
      />

      <FormField
        control={form.control}
        name="email"
        render={({ field }) => (
          <FormInputEmail
            formItemClassName="col-span-12 sm:col-span-4"
            label="Email"
            {...field}
            disabled={!isEditable}
          />
        )}
      />

      <FormField
        control={form.control}
        name="billing_email_address"
        render={({ field }) => (
          <FormInputEmail
            formItemClassName="col-span-12 sm:col-span-4"
            label="Billing Email"
            {...field}
            disabled={!isEditable}
          />
        )}
      />

      <FormField
        control={form.control}
        name="payable_to"
        render={({ field }) => (
          <FormInput
            formItemClassName="col-span-12 sm:col-span-4"
            label="Payable To"
            {...field}
            disabled={!isEditable}
          />
        )}
      />

      <FormField
        control={form.control}
        name="online_payment_url"
        render={({ field }) => (
          <FormInputUrl
            formItemClassName="col-span-12 sm:col-span-4"
            label="Online Payment URL"
            {...field}
            disabled={!isEditable}
          />
        )}
      />

      <div className="col-span-12 sm:col-span-4">
        <FormField
          control={form.control}
          name="hide_online_payments"
          render={({ field: { value, onChange } }) => (
            <div className="flex w-fit flex-col gap-3">
              <Label className="-mt-1">Hide Online Payments</Label>
              <LabeledSwitch checked={value} disabled={!isEditable} onCheckedChange={onChange} label="Yes" />
            </div>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="agreement_statement"
        render={({ field }) => (
          <FormTextarea formItemClassName="col-span-12" label="Agreement Statement" {...field} disabled={!isEditable} />
        )}
      />

      <FormField
        control={form.control}
        name="invoice_disclaimer"
        render={({ field }) => (
          <RichText
            label="Invoice Disclaimer"
            value={field.value}
            onChange={field.onChange}
            disabled={!isEditable}
            inline={!isEditable}
            mediaFolderPath={`brand/${brandId}/invoice-disclaimer`}
            className="col-span-12"
          />
        )}
      />
    </>
  );
}
